// mediaEngagementService.js — real appreciations + reflections on a photo,
// keyed by the photo's own raw storage URL (media_key) so the same photo
// carries its engagement across a listing, a showcase, and an Edit article,
// not just within one surface. Backs the "Appreciations & Reflections" UI in
// src/components/profile/Lightbox.jsx. See migration
// supabase/migrations/20260912_media_engagement.sql for schema + RLS —
// public read, write only as an authenticated user and only as yourself.
import { supabase } from '../lib/supabaseClient.js';

// Batch-load appreciation counts + this viewer's own like + reflections for
// every photo in a gallery, in ONE round trip per table (not per photo).
export async function fetchMediaEngagement(mediaKeys, viewerUserId) {
  const keys = [...new Set((mediaKeys || []).filter(Boolean))];
  const out = {};
  keys.forEach(k => { out[k] = { likeCount: 0, likedByMe: false, comments: [] }; });
  if (!keys.length) return out;

  const [{ data: apps }, { data: refs }] = await Promise.all([
    supabase.from('media_appreciations').select('media_key, user_id').in('media_key', keys),
    supabase.from('media_reflections')
      .select('id, media_key, author_name, author_type, body, created_at')
      .in('media_key', keys)
      .order('created_at', { ascending: true }),
  ]);

  (apps || []).forEach(a => {
    const row = out[a.media_key];
    if (!row) return;
    row.likeCount += 1;
    if (viewerUserId && a.user_id === viewerUserId) row.likedByMe = true;
  });
  (refs || []).forEach(r => {
    const row = out[r.media_key];
    if (row) row.comments.push(r);
  });
  return out;
}

// Toggle an appreciation for the signed-in user. Returns the new liked state,
// throwing if the write itself failed (RLS denial, network error, etc.) so
// the caller's optimistic UI update can be rolled back instead of lying.
export async function toggleAppreciation(mediaKey, userId) {
  const { data: existing } = await supabase
    .from('media_appreciations')
    .select('id')
    .eq('media_key', mediaKey)
    .eq('user_id', userId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase.from('media_appreciations').delete().eq('id', existing.id);
    if (error) throw error;
    return false;
  }
  const { error } = await supabase.from('media_appreciations').insert({ media_key: mediaKey, user_id: userId });
  if (error) throw error;
  return true;
}

// Add a reflection (comment). Returns the inserted row, or null on failure.
export async function addReflection(mediaKey, { userId, authorName, authorType, body }) {
  const text = (body || '').trim();
  if (!text) return null;
  const { data, error } = await supabase
    .from('media_reflections')
    .insert({ media_key: mediaKey, user_id: userId, author_name: authorName, author_type: authorType, body: text })
    .select('id, media_key, author_name, author_type, body, created_at')
    .single();
  if (error) { console.warn('[mediaEngagementService] addReflection failed', error); return null; }
  return data;
}
